import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import type { Parchment } from 'quill/core';
import Quill, { AttributeMap, Delta } from 'quill/core';
import React from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import Badge from '../../../components/Badge';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import addIcon from '../../../icons/non-prefilled/16x16/add.svg';
import useIsLayoutSize from '../../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../../post/utils/useLayoutSize';
import { UIActions } from '../../../ui/uiSlice';
import useIsPublicView from '../../../utils/useIsPublicView';
import { useAssetTokenContext } from '../../AssetTokenContext';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import { EDITOR_IMAGE_MIME_TYPES } from '../../editorImageMimeTypes';
import ImageBlot, { compactCarouselItem } from '../../formats/ImageBlot';
import { CAROUSEL_IMAGE_ATTRIBUTE } from '../../image/ImageNode';
import type { CarouselItem } from '../../image/types';
import EditorModal from '../../modals/EditorModal';
import { expandIcon, imageLeftIcon, imageRightIcon } from '../editorIcons';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import * as styles from './ImageToolbar.module.scss';
import Toolbar, {
  ToolbarOverflowMenu,
  useToolbarOverflowMenuClose,
} from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarHeightButtonGroup from './buttons/ToolbarHeightButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import useBlotHighlightedClass from './useBlotHighlightedClass';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type UpdateImage = (change: {
  width?: number | null;
  attributes: AttributeMap;
}) => void;

interface ImageToolbarOverflowMenuProps {
  onViewFullscreen: () => void;
  blot: ImageBlot;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const ImageToolbarOverflowMenu = ({
  blot,
  onViewFullscreen,
}: ImageToolbarOverflowMenuProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const closeOverflowMenu = useToolbarOverflowMenuClose();

  return (
    <>
      <MenuItem
        id="aria-add-image-menu-item"
        onClick={() => fileInputRef.current?.click()}
        icon={addIcon}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={EDITOR_IMAGE_MIME_TYPES.join(',')}
          onChange={(e) => {
            if (e.target.files) {
              blot.uploadFiles(Array.from(e.target.files));
            }
          }}
          aria-labelledby="aria-add-image-menu-item"
        />
        Add images
        <Badge className={styles.betaBadgeContainer} color="red">
          New
        </Badge>
      </MenuItem>
      <MenuItem
        icon={expandIcon}
        onClick={() => {
          onViewFullscreen();
          closeOverflowMenu();
        }}
      >
        View expanded
      </MenuItem>
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>
        Delete {blot.isCarousel() ? 'carousel' : 'image'}
      </DeleteMenuItem>
    </>
  );
};

interface BaseImageToolbarProps {
  hoveredBlot: ImageBlot;
  formats: Formats;
  firstImageWidth?: number | null;
  updateImage: UpdateImage;
  setPersist?: (persist: boolean) => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseImageToolbar = ({
  hoveredBlot,
  formats,
  firstImageWidth,
  updateImage,
  setPersist,
}: BaseImageToolbarProps) => {
  const readOnly = useQuillReadOnly();
  const dispatch = useAppDispatch();
  useBlotHighlightedClass(hoveredBlot);
  const { entityAssetToken } = useAssetTokenContext();

  const isPublicView = useIsPublicView();
  const isCarousel = hoveredBlot.isCarousel();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const viewFullscreen = () => {
    const { images, selectedIndex } = hoveredBlot.getData();
    if (entityAssetToken && Array.isArray(images)) {
      dispatch(
        UIActions.openModal(EditorModal, {
          carousel: {
            images,
            selectedIndex,
            entityAssetToken,
          },
          underlayType: 'carouselSlider',
        }),
      );
    }
  };

  if (readOnly && isPublicView) {
    return (
      <Toolbar>
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={expandIcon}
            tooltip="View&nbsp;expanded"
            onClick={viewFullscreen}
            newTab
          >
            Expand
          </ToolbarButton>
        </ToolbarButtonGroup>
      </Toolbar>
    );
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu
            onMenuOpenChange={setPersist}
            rightAlignOverflowMenu
          >
            <ImageToolbarOverflowMenu
              blot={hoveredBlot}
              onViewFullscreen={viewFullscreen}
            />
          </ToolbarOverflowMenu>
        )
      }
    >
      {readOnly && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={expandIcon}
            tooltip="View expanded"
            onClick={viewFullscreen}
          >
            Expand
          </ToolbarButton>
        </ToolbarButtonGroup>
      )}
      {!readOnly && (
        <>
          {!(isCarousel && isMobile) && (
            <ToolbarSpanButtonGroup
              allowHalfWidth={!isCarousel}
              disallowDefaultWidthActive={
                firstImageWidth != null || !!formats.float
              }
              span={formats.span}
              onSpanChange={(newSpan) => {
                if (newSpan === 'half') {
                  const img = hoveredBlot.domNode.querySelector(
                    `img[${CAROUSEL_IMAGE_ATTRIBUTE}]`,
                  );
                  const naturalWidth =
                    img instanceof HTMLImageElement
                      ? img.naturalWidth
                      : undefined;
                  updateImage({
                    width: naturalWidth ? naturalWidth / 2 : undefined,
                    attributes: { span: 'half', float: null },
                  });
                } else {
                  updateImage({
                    width: null,
                    attributes: { span: newSpan, float: null },
                  });
                }
              }}
            />
          )}
          {isCarousel && (
            <ToolbarHeightButtonGroup
              height={formats.height}
              onHeightChange={(newHeight) =>
                updateImage({
                  attributes: { height: newHeight },
                })
              }
            />
          )}
          {!isCarousel && (
            <ToolbarButtonGroup aria-label="Float options" role="radiogroup">
              <ToolbarButton
                accessibleType="radio"
                icon={imageLeftIcon}
                tooltip="Float left"
                active={formats.float === 'left'}
                onClick={() =>
                  updateImage({
                    attributes: { span: null, float: 'left' },
                  })
                }
              />
              <ToolbarButton
                accessibleType="radio"
                icon={imageRightIcon}
                tooltip="Float right"
                active={formats.float === 'right'}
                onClick={() =>
                  updateImage({
                    attributes: { span: null, float: 'right' },
                  })
                }
              />
            </ToolbarButtonGroup>
          )}
        </>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  firstImageWidth?: number;
  naturalWidth?: number;
  formats: Record<string, string[] | string | null | undefined>;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  isHoveredBlot(blot: Parchment.Blot) {
    return blot.statics.blotName === ImageBlot.blotName;
  },
  getContext(blot: ImageBlot) {
    const value = blot.value().image;

    if (Array.isArray(value) && value.length > 1) {
      return {
        formats: blot.formats(),
      };
    }

    const firstImageWidth = Array.isArray(value) ? value[0]?.width : undefined;

    return {
      firstImageWidth,
      formats: blot.formats(),
    };
  },
};

const checkIsCarouselItems = (
  imageValue: unknown,
): imageValue is CarouselItem[] => Array.isArray(imageValue);

const ImageToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    setPersist,
    context: { firstImageWidth, formats } = {
      firstImageWidth: undefined,
      formats: undefined,
    },
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const updateImage: UpdateImage = (change) => {
    if (!(hoveredBlot instanceof BlockEmbed)) return;
    const delta = new Delta().retain(hoveredBlot.offset(hoveredBlot.scroll));
    let retain: 1 | { image: CarouselItem[] } = 1;
    const currentFormats = hoveredBlot.formats();
    const attributes = AttributeMap.diff(
      currentFormats,
      AttributeMap.compose(currentFormats, change.attributes, false),
    );

    if (typeof change.attributes.height !== 'undefined') {
      const value = hoveredBlot.value().image;
      if (checkIsCarouselItems(value)) {
        retain = 1;
      }
    } else if (typeof change.width !== 'undefined') {
      const value = hoveredBlot.value().image;
      if (checkIsCarouselItems(value)) {
        const firstImage = value[0];
        if (firstImage) {
          const newValue = [
            compactCarouselItem({
              ...firstImage,
              width: change.width ?? undefined,
            }),
            ...value.slice(1),
          ];
          if (!_.isEqual(newValue, value)) {
            retain = {
              image: newValue,
            };
          }
        }
      }
    }

    if (quill && (retain !== 1 || !_.isEmpty(attributes))) {
      quill.updateContents(
        delta.retain(retain, attributes),
        Quill.sources.USER,
      );
    }
  };

  return (
    <FloatingToolbar.Toolbar
      leftAligned={formats?.float === 'left'}
      bounds={bounds}
    >
      {hoveredBlot instanceof ImageBlot && formats && (
        <BaseImageToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          firstImageWidth={firstImageWidth}
          updateImage={updateImage}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default ImageToolbar;
